import {IDataTableData} from "@/features/application/api/applicationModels";
import {cloneDeep} from "lodash";

export interface SeriesMapping {
    name: string;
    type?: string;
    xColumn?: string;
    yColumn?: string;
    categoryColumn?: string;
    businessUnitColumn?: string;
    valueColumn?: string;
    zColumn?: string;
    yAxis?: number;
    stacking?: string;
    dataLabels?: any;
}

// SeriesMapping is a property generated by an LLM that allows for dynamic mapping of table data to chart series.
export interface ChartConfigWithMapping extends Highcharts.Options {
    seriesMapping?: SeriesMapping[];
}

export function populateChartData(
    chartConfig: ChartConfigWithMapping,
    dataRows: IDataTableData[]
): ChartConfigWithMapping | undefined {
    try {
        let chartConfigCopy = cloneDeep(chartConfig);
        // Fix for case where seriesMapping is hallucinated in yAxis
        chartConfigCopy = fixSeriesMappingLocation(chartConfigCopy);
        // Fix for case where multiple business units are rendered as a flat
        // column chart instead of one line per business unit
        chartConfigCopy = autoGroupByBusinessUnit(chartConfigCopy, dataRows);
        // Fix for case where column charts are missing categoryColumn
        chartConfigCopy = autoSetCategoryColumn(chartConfigCopy, dataRows);

        const {seriesMapping} = chartConfigCopy;
        if (!seriesMapping) {
            return undefined;
        }
        // Fix for case where series data is hallucinated
        delete chartConfigCopy.series;

        const chartType = chartConfigCopy.chart?.type;
        const isGroupedByBusinessUnit =
            chartType !== "pie" &&
            chartType !== "bubble" &&
            chartType !== "scatter" &&
            seriesMapping.some((m) => m.businessUnitColumn);

        const categoryColumn = extractCategoryColumn(seriesMapping);
        let categories: string[] | undefined;
        if (categoryColumn) {
            const rawCategories = dataRows
                .map((row) => row[categoryColumn])
                .filter((val) => val !== null && val !== undefined)
                .map(String);
            // Grouping into one series per business unit needs a deduplicated
            // shared category axis (e.g. one "0-7 days" tick, not one per unit)
            categories = isGroupedByBusinessUnit
                ? Array.from(new Set(rawCategories))
                : rawCategories;
            chartConfigCopy = applyCategoriesToXAxis(chartConfigCopy, categories);
        }

        if (isGroupedByBusinessUnit) {
            const {series, mappings} = buildGroupedSeriesAndMappings(
                seriesMapping,
                dataRows,
                categories
            );
            chartConfigCopy.series = series;
            chartConfigCopy.seriesMapping = mappings;
            return chartConfigCopy;
        }

        const series = buildSeriesFromMappings(
            chartType,
            seriesMapping,
            dataRows
        );

        chartConfigCopy.series = series;
        return chartConfigCopy;
    } catch (error) {
        return undefined;
    }
}

function fixSeriesMappingLocation(
    config: ChartConfigWithMapping
): ChartConfigWithMapping {
    if (!config.seriesMapping && Array.isArray(config.yAxis)) {
        for (let i = config.yAxis.length - 1; i >= 0; i--) {
            const axis = config.yAxis[i];
            if (axis && typeof axis === "object" && "seriesMapping" in axis) {
                config.seriesMapping = (axis as any).seriesMapping;
                config.yAxis.splice(i, 1);
                break;
            }
        }
    }
    return config;
}

function autoSetCategoryColumn(
    config: ChartConfigWithMapping,
    dataRows: IDataTableData[]
): ChartConfigWithMapping {
    // A fix for hallucination based on an observed case
    // For column charts, automatically set categoryColumn to "BusinessUnit" if not specified,
    // remove xColumn (column charts use categories, not x-values), and set xAxis type to "category"
    const isColumnChart = config.chart?.type === "column";
    if (
        isColumnChart &&
        config.seriesMapping &&
        dataRows.length > 0 &&
        "BusinessUnit" in dataRows[0]
    ) {
        config.seriesMapping = config.seriesMapping.map((mapping) => {
            const updatedMapping = {...mapping};
            if (!updatedMapping.categoryColumn) {
                updatedMapping.categoryColumn = "BusinessUnit";
            }
            // Remove xColumn for column charts since they use categoryColumn instead
            delete updatedMapping.xColumn;
            return updatedMapping;
        });

        // Set xAxis type to "category" for column charts
        if (!config.xAxis) {
            config.xAxis = {type: "category"};
        } else if (Array.isArray(config.xAxis)) {
            config.xAxis[0] = {...config.xAxis[0], type: "category"};
        } else {
            config.xAxis = {...config.xAxis, type: "category"};
        }
    }
    return config;
}

const BUSINESS_UNIT_COLUMN_CANDIDATES = ["BusinessUnit", "business_unit", "Business Unit"];

function detectBusinessUnitColumn(dataRows: IDataTableData[]): string | undefined {
    if (dataRows.length === 0) {
        return undefined;
    }
    return BUSINESS_UNIT_COLUMN_CANDIDATES.find((col) => col in dataRows[0]);
}

function countDistinctValues(dataRows: IDataTableData[], column: string): number {
    return new Set(
        dataRows
            .map((row) => row[column])
            .filter((val) => val !== null && val !== undefined)
    ).size;
}

// A fix for the case where multiple business units are rendered as a flat
// column chart (categories become every business-unit/bucket combination,
// e.g. "0-7 days" repeated once per unit). When there's a separate axis
// column (a date or bucket), one line per business unit reads far better.
function autoGroupByBusinessUnit(
    config: ChartConfigWithMapping,
    dataRows: IDataTableData[]
): ChartConfigWithMapping {
    const businessUnitColumn = detectBusinessUnitColumn(dataRows);
    if (!businessUnitColumn || !config.seriesMapping) {
        return config;
    }

    const isBarLikeChart = ["column", "bar", undefined].includes(config.chart?.type);
    const hasMultipleBusinessUnits =
        countDistinctValues(dataRows, businessUnitColumn) > 1;
    const hasSeparateAxisColumn = config.seriesMapping.some(
        (mapping) =>
            (mapping.categoryColumn && mapping.categoryColumn !== businessUnitColumn) ||
            (mapping.xColumn && mapping.xColumn !== businessUnitColumn)
    );

    if (!isBarLikeChart || !hasMultipleBusinessUnits || !hasSeparateAxisColumn) {
        return config;
    }

    return {
        ...config,
        chart: {...config.chart, type: "line"},
        seriesMapping: config.seriesMapping.map((mapping) => ({
            ...mapping,
            type: "line",
            businessUnitColumn: mapping.businessUnitColumn ?? businessUnitColumn
        }))
    };
}

function extractCategoryColumn(seriesMappings: SeriesMapping[]): string | undefined {
    return seriesMappings.find((m) => m.categoryColumn)?.categoryColumn;
}

function uniqueColumnValues(dataRows: IDataTableData[], column: string): string[] {
    const seen = new Set<string>();
    const values: string[] = [];
    for (const row of dataRows) {
        const val = row[column];
        if (val === null || val === undefined) continue;
        const strVal = String(val);
        if (!seen.has(strVal)) {
            seen.add(strVal);
            values.push(strVal);
        }
    }
    return values;
}

function applyCategoriesToXAxis(
    config: ChartConfigWithMapping,
    categories: string[]
): ChartConfigWithMapping {
    if (!config.xAxis) {
        config.xAxis = {};
    }

    if (Array.isArray(config.xAxis)) {
        config.xAxis[0] = {...config.xAxis[0], categories};
    } else {
        config.xAxis = {...config.xAxis, categories};
    }

    return config;
}

function buildSeriesFromMappings(
    chartType: string | undefined,
    seriesMappings: SeriesMapping[],
    dataRows: IDataTableData[]
): Highcharts.SeriesOptionsType[] {
    if (chartType === "pie") {
        return buildPieSeries(seriesMappings, dataRows);
    } else if (chartType === "bubble") {
        return buildBubbleSeries(seriesMappings, dataRows);
    } else {
        return buildStandardSeries(seriesMappings, dataRows);
    }
}

function buildPieSeries(
    seriesMappings: SeriesMapping[],
    dataRows: IDataTableData[]
): Highcharts.SeriesOptionsType[] {
    const pieData = seriesMappings.map((mapping) => {
        // This is an opinionated way to solve the problem of overly fragmented pie charts
        const avg = averageColumnValues(dataRows, mapping.yColumn!);
        return {name: mapping.name, y: avg};
    });

    return [
        {
            type: "pie",
            data: pieData
        } as Highcharts.SeriesOptionsType
    ];
}

function averageColumnValues(
    dataRows: IDataTableData[],
    columnName: string
): number {
    const values = dataRows
        .map((row) => row[columnName])
        .filter((val) => val !== null && val !== undefined)
        .map(parseNumericValue)
        .filter((val) => !isNaN(val));
    if (values.length === 0) return 0;
    return values.reduce((sum, val) => sum + val, 0) / values.length;
}

function buildBubbleSeries(
    seriesMappings: SeriesMapping[],
    dataRows: IDataTableData[]
): Highcharts.SeriesOptionsType[] {
    return seriesMappings.map((mapping) => {
        const bubbleData = extractBubbleData(dataRows, mapping);
        return createSeriesObject("bubble", mapping, bubbleData);
    });
}

function buildStandardSeries(
    seriesMappings: SeriesMapping[],
    dataRows: IDataTableData[]
): Highcharts.SeriesOptionsType[] {
    return seriesMappings.map((mapping) => {
        const data = extractSeriesData(dataRows, mapping);
        return createSeriesObject(mapping.type, mapping, data);
    });
}

// Splits each mapping with a businessUnitColumn into one series per distinct
// business unit, and returns matching seriesMapping entries (name = unit
// value) so downstream tooltip/legend lookups keyed by series name still work.
function buildGroupedSeriesAndMappings(
    seriesMappings: SeriesMapping[],
    dataRows: IDataTableData[],
    categories: string[] | undefined
): {series: Highcharts.SeriesOptionsType[]; mappings: SeriesMapping[]} {
    const series: Highcharts.SeriesOptionsType[] = [];
    const mappings: SeriesMapping[] = [];

    seriesMappings.forEach((mapping) => {
        const {businessUnitColumn, categoryColumn, xColumn, yColumn} = mapping;
        if (!businessUnitColumn || !yColumn || (!categoryColumn && !xColumn)) {
            series.push(createSeriesObject(mapping.type, mapping, extractSeriesData(dataRows, mapping)));
            mappings.push(mapping);
            return;
        }

        const resolvedCategories = categoryColumn
            ? categories ?? uniqueColumnValues(dataRows, categoryColumn)
            : undefined;

        uniqueColumnValues(dataRows, businessUnitColumn).forEach((groupName) => {
            const groupRows = dataRows.filter(
                (row) => String(row[businessUnitColumn]) === groupName
            );
            const groupMapping: SeriesMapping = {
                ...mapping,
                name: groupName,
                type: mapping.type ?? "line"
            };
            const data = categoryColumn
                ? alignValuesToCategories(groupRows, categoryColumn, yColumn, resolvedCategories!)
                : extractXYPairs(groupRows, xColumn!, yColumn);

            series.push(createSeriesObject(groupMapping.type, groupMapping, data));
            mappings.push(groupMapping);
        });
    });

    return {series, mappings};
}

function alignValuesToCategories(
    groupRows: IDataTableData[],
    categoryColumn: string,
    yColumn: string,
    categories: string[]
): (number | null)[] {
    const byCategory = new Map<string, number>();
    groupRows.forEach((row) => {
        const cat = row[categoryColumn];
        const yVal = row[yColumn];
        if (cat === null || cat === undefined || yVal === null || yVal === undefined) {
            return;
        }
        const parsed = parseNumericValue(yVal);
        if (!isNaN(parsed)) {
            byCategory.set(String(cat), parsed);
        }
    });
    return categories.map((cat) => (byCategory.has(cat) ? byCategory.get(cat)! : null));
}

function extractBubbleData(
    dataRows: IDataTableData[],
    mapping: SeriesMapping
): any[] {
    const {xColumn, yColumn, zColumn} = mapping;

    if (!xColumn || !yColumn || !zColumn) {
        return [];
    }

    return dataRows
        .map((row) => {
            const xVal = row[xColumn];
            const yVal = row[yColumn];
            const zVal = row[zColumn];

            if (
                xVal === null ||
                xVal === undefined ||
                yVal === null ||
                yVal === undefined ||
                zVal === null ||
                zVal === undefined
            ) {
                return null;
            }
            const processedXVal = parseNumericValue(xVal);
            const processedYVal = parseNumericValue(yVal);
            const processedZVal = parseNumericValue(zVal);

            if (
                isNaN(processedXVal) ||
                isNaN(processedYVal) ||
                isNaN(processedZVal)
            ) {
                return null;
            }

            return {x: processedXVal, y: processedYVal, z: processedZVal};
        })
        .filter((point) => point !== null);
}

function extractSeriesData(
    dataRows: IDataTableData[],
    mapping: SeriesMapping
): any[] {
    const {xColumn, yColumn, categoryColumn} = mapping;
    const hasCategoryColumn = !!categoryColumn;

    if (xColumn && yColumn && !hasCategoryColumn) {
        return extractXYPairs(dataRows, xColumn, yColumn);
    }

    if (yColumn) {
        return extractYValues(dataRows, yColumn);
    }

    return [];
}

function extractXYPairs(
    dataRows: IDataTableData[],
    xColumn: string,
    yColumn: string
): any[] {
    const pairs = dataRows
        .map((row) => {
            const xVal = row[xColumn];
            const yVal = row[yColumn];

            if (
                xVal === null ||
                xVal === undefined ||
                yVal === null ||
                yVal === undefined
            ) {
                return null;
            }

            const processedXVal = parseXValue(xVal);
            const processedYVal = parseNumericValue(yVal);

            if (isNaN(processedYVal)) {
                return null;
            }

            return [processedXVal, processedYVal];
        })
        .filter((pair) => pair !== null);

    return sortByXValue(pairs);
}

function extractYValues(dataRows: IDataTableData[], yColumn: string): any[] {
    return dataRows.map((row) => {
        const yVal = row[yColumn];
        if (yVal === null || yVal === undefined) {
            return 0;
        }
        const processedYVal = parseNumericValue(yVal);
        return isNaN(processedYVal) ? 0 : processedYVal;
    });
}

function parseXValue(value: any): any {
    if (typeof value === "string") {
        const parsedDate = Date.parse(value);
        if (!isNaN(parsedDate)) {
            return parsedDate;
        }
    }

    if (value instanceof Date) {
        return value.getTime();
    }

    return value;
}

function parseNumericValue(value: any): number {
    return typeof value === "number" ? value : parseFloat(String(value));
}

function sortByXValue(pairs: any[]): any[] {
    return pairs.sort((a, b) => {
        const xA = typeof a[0] === "number" ? a[0] : 0;
        const xB = typeof b[0] === "number" ? b[0] : 0;
        return xA - xB;
    });
}

function createSeriesObject(
    seriesType: string | undefined,
    mapping: SeriesMapping,
    data: any[]
): Highcharts.SeriesOptionsType {
    const seriesObj: any = {
        name: mapping.name,
        data: data
    };

    if (seriesType) {
        seriesObj.type = seriesType;
    }

    const additionalProperties = ["yAxis", "stacking", "dataLabels"];
    for (const key of additionalProperties) {
        if (key in mapping) {
            let value = (mapping as any)[key];
            // For yAxis, only convert numeric strings (like "0", "1") to numbers
            // Leave string IDs (like "sales", "hours") as-is since Highcharts supports both
            if (key === "yAxis" && typeof value === "string") {
                const numValue = parseInt(value, 10);
                if (!isNaN(numValue) && value === numValue.toString()) {
                    value = numValue;
                }
            }
            seriesObj[key] = value;
        }
    }

    return seriesObj as Highcharts.SeriesOptionsType;
}
